import * as React from "react"
import { useQuery } from "react-query"
import { Flex, HStack, Box, Heading } from "@chakra-ui/react"
import { Sidebar } from "../components/Sidebar"
import { fetcher } from "../lib/fetcher"
import { useOrgs } from "../components/providers/OrgProvider"
import { AlertsResponse, AlertType, AlertTypesResponse } from "../lib/types/alert"
import { Order } from "../lib/types/shared"
import { ALERT_TABLE_PAGE_SIZE, ALERT_UNSELECTED_FILTERS_KEY, TOPBAR_HEIGHT, WORLD_BBOX } from "../lib/config"
import { AlertsSidebar } from "../components/AlertsSidebar"
import { AlertsMap } from "../components/AlertsMap"
import { Bbox, Coord, createBbox } from "../lib/helpers/map"
import { useDocumentTitle } from "../lib/hooks/useDocumentTitle"
import { useLocalStorage } from "../lib/hooks/useLocalStorage"
import { useMapBBox } from "../components/providers/MapBBoxProvider"
import { useLocation, useParams } from "react-router-dom"

export const Alerts = () => {
  const { pathname } = useLocation()
  const { id } = useParams()
  const isBikeAlertPage = !!id && pathname?.includes("assets")
  const pageTitle = isBikeAlertPage ? "Asset Alerts" : "Alerts"

  useDocumentTitle(pageTitle)

  const { currentOrgs } = useOrgs()
  const [order, setOrder] = React.useState<Order>({
    order: "-",
    orderBy: "created",
  })
  const [selectedAlertUuid, setSelectedAlertUuid] = React.useState<string>()
  const [selectedBikeUuid, setSelectedBikeUuid] = React.useState<string>()
  const [bbox, setBbox] = React.useState<Bbox | undefined>()
  const [page, setPage] = React.useState<number>(1)
  const [alertFilters, setAlertFilters] = React.useState<AlertType[]>([])
  const [unselectedFilters, setUnselectedFilters] = useLocalStorage<AlertType[]>(
    ALERT_UNSELECTED_FILTERS_KEY,
    [],
  )
  const { currentBBox } = useMapBBox()

  const currentOrgsUuids = currentOrgs.map(({ uuid }) => uuid)

  const { data: alertTypeData, isLoading: alertTypeLoading } = useQuery<AlertTypesResponse, string>(
    `dashboard/?organizations=${currentOrgsUuids.join()}`,
    { staleTime: Infinity },
  )

  // ZONE ALERTS - check zone type alerts are in here
  const defaultAlertFilters = alertTypeData?.count_alerts_by_type.map((a) => a.alert_type) || []

  React.useEffect(() => {
    if (!alertTypeLoading && alertTypeData)
      setAlertFilters(
        alertTypeData.count_alerts_by_type
          .map((a) => a.alert_type)
          .filter((type) => !unselectedFilters.includes(type)),
      )
  }, [alertTypeLoading, alertTypeData, unselectedFilters])

  const handleSetAlertFilters = (types: AlertType[]) => {
    setAlertFilters(types)
    setUnselectedFilters(defaultAlertFilters.filter((d) => !types.includes(d)))
  }

  const alertTypeParams = alertFilters?.reduce((acc, alert) => (acc += "&alert_type=" + alert), "")
  const orderParams = order.order + order.orderBy

  const bikeIdFilter = isBikeAlertPage ? `&bicycle=${id}` : ""

  const url = `alerts/?page_size=${ALERT_TABLE_PAGE_SIZE}${alertTypeParams}&ordering=${orderParams}&state=new&organizations=${currentOrgsUuids.join()}${bikeIdFilter}`
  const fields = `&fields=extra,alert_type,uuid,created,state,organization`
  const getAlerts = React.useCallback(
    (currentPage: number) => fetcher(url + fields + "&page=" + currentPage),
    [url, fields],
  )

  const {
    data: alertData,
    isLoading,
    refetch: refetchAlerts,
  } = useQuery<AlertsResponse, [string, number]>([url, page], () => getAlerts(page), {
    enabled: !!alertTypeData,
  })

  const alerts = alertData?.results

  React.useEffect(() => {
    if (isLoading || !alerts) return
    if (currentBBox) {
      return setBbox(currentBBox)
    }
    if (alerts.length === 0) {
      return setBbox(WORLD_BBOX)
    }
    const locationCoords = alerts
      .filter((a) => !!a.extra?.location?.coordinates)
      .map((a) => a.extra?.location?.coordinates as Coord)
    if (locationCoords.length > 0) {
      const newBbox = createBbox(locationCoords)
      setBbox(newBbox)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alerts, isLoading])

  React.useEffect(() => {
    setPage(1)
  }, [alertFilters, order])

  return (
    <Box position="relative">
      <Flex align="center" justify="space-between" p={6} h={TOPBAR_HEIGHT}>
        <HStack spacing={6} align="center">
          <Heading as="h3" color="gray.700">
            {pageTitle}
          </Heading>
        </HStack>
      </Flex>
      <Sidebar sidebarKey="noa:alerts:map" styleControl>
        <AlertsSidebar
          {...{
            alerts: alerts || [],
            order,
            refetchAlerts,
            page,
            pageSize: ALERT_TABLE_PAGE_SIZE,
            defaultAlertFilters,
            alertFilters,
            isLoading,
          }}
          count={alertData?.count}
          onSetPage={setPage}
          onSetOrder={setOrder}
          onSetSelectedAlert={setSelectedAlertUuid}
          onSetSelectedBike={setSelectedBikeUuid}
          onSetAlertFilters={handleSetAlertFilters}
          onSetBbox={setBbox}
        />
      </Sidebar>
      {bbox && (
        <AlertsMap
          {...{
            refetchAlerts,
            selectedAlertUuid,
            onDeselectAlert: () => {
              setSelectedAlertUuid(undefined)
            },
            alerts: alerts || [],
            bbox,
            isLoading,
            selectedBikeUuid,
            setSelectedBikeUuid,
            onDeselectBike: () => {
              setSelectedBikeUuid(undefined)
            },
          }}
        />
      )}
    </Box>
  )
}
